import { useState } from "react";
import { ImageIcon, Trash2, Upload } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import type { SiteContent } from "../../../api/types";
import { getAt } from "../../../model/content";
import { hostImageSlots, type ImageSlot } from "../../../model/hostNodes";
import { CropUploadDialog } from "./CropUploadDialog";
import { NO_IMAGE_TEXT } from "./hostLabels";
import { ProxiedImage } from "./ProxiedImage";

/**
 * 编辑页右列的图片区(host 模式,A.51 / A.53 / A.57):每格 = 当前图 + 「上传」(裁剪 → base64 → uploadImage)+「清空」。
 * ★ 空着的格发布时对应位置的模板原图会被清空(A.51),照实写,不说「用模板默认图」。
 * ★ 上传回来的 url 写进本地草稿(置脏),保存草稿才落库 —— 与文字节点同一条路。
 */
export function HostImagePanel({
  content,
  upload,
  onSet,
  disabled,
}: {
  content: SiteContent;
  /** 裸 base64 + 这一格 → 完整 url(页面里补宿主根)。 */
  upload: (slot: ImageSlot, base64: string) => Promise<string>;
  onSet: (path: string, url: string | null) => void;
  disabled?: boolean;
}) {
  const [active, setActive] = useState<ImageSlot | null>(null);
  const slots = hostImageSlots(content);

  return (
    <section
      aria-label="图片"
      className={cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground">图片</h2>
        <p className="mt-0.5 text-2xs text-muted-foreground">
          模板里原企业的图片发布时会被清空;这里上传的换上去。空着的位置发布后不显示图。
        </p>
      </div>
      <ul className="grid gap-3 sm:grid-cols-2">
        {slots.map((slot) => {
          const raw = getAt(content, slot.path);
          const value = typeof raw === "string" ? raw : "";
          return (
            <li
              key={slot.path}
              role="group"
              aria-label={slot.label}
              data-path={slot.path}
              className="flex gap-3 rounded-md border border-border/70 p-2"
            >
              <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded bg-muted">
                {value ? (
                  <ProxiedImage
                    value={value}
                    alt={slot.label}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex flex-col items-center gap-0.5 text-2xs text-muted-foreground">
                    <ImageIcon className="h-4 w-4" aria-hidden />
                    {NO_IMAGE_TEXT}
                  </span>
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span className="truncate text-xs font-medium text-foreground">{slot.label}</span>
                <span className="text-2xs text-muted-foreground">
                  {value ? "已设置" : "未设置"}
                </span>
                <div className="mt-auto flex gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    disabled={disabled}
                    onClick={() => setActive(slot)}
                  >
                    <Upload className="h-3 w-3" />
                    上传
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    disabled={disabled || !value}
                    aria-label={`清空:${slot.label}`}
                    onClick={() => onSet(slot.path, null)}
                  >
                    <Trash2 className="h-3 w-3" />
                    清空
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <CropUploadDialog
        open={active !== null}
        onOpenChange={(o) => {
          if (!o) setActive(null);
        }}
        title={active ? `上传 · ${active.label}` : "裁剪后上传"}
        defaultAspect={active?.aspect ?? null}
        upload={(b64) =>
          active ? upload(active, b64) : Promise.reject(new Error("没有选中图片位"))
        }
        onDone={(url) => {
          if (active) onSet(active.path, url);
        }}
      />
    </section>
  );
}
